/**
 * useSMEPeriodic — SME Periodic Workspace Data Hook
 *
 * Manages: period selection, monthly data, evolution trend, YTD, T12M.
 * This is NOT useProjectWorkspace — SME is a recurring operating workspace.
 */
import { useState, useEffect, useCallback } from "react";
import client from "@/api/client";

export function useSMEPeriodic() {
  const now   = new Date();
  const [selectedYear,  setSelectedYear]  = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12

  const [currentPeriod,  setCurrentPeriod]  = useState(null);
  const [evolution,      setEvolution]       = useState([]);
  const [ytdData,        setYtdData]         = useState(null);
  const [trailing12,     setTrailing12]      = useState(null);
  const [allPeriods,     setAllPeriods]      = useState([]);
  const [loading,        setLoading]         = useState(true);
  const [error,          setError]           = useState(null);

  const periodLabel = `${selectedYear}-${String(selectedMonth).padStart(2,"0")}`;

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [evRes, ytdRes, t12Res, periodsRes] = await Promise.all([
        client.get("/sme/periodic/evolution?months=12"),
        client.get(`/sme/periodic/ytd?year=${selectedYear}`),
        client.get("/sme/periodic/trailing12"),
        client.get("/sme/periodic/periods"),
      ]);
      setEvolution(evRes.data?.periods || []);
      setYtdData(ytdRes.data || null);
      setTrailing12(t12Res.data || null);
      setAllPeriods(periodsRes.data?.periods || []);

      // Load current period if it exists
      const exists = (periodsRes.data?.periods || []).find(p => p.period_label === periodLabel);
      if (exists) {
        const detail = await client.get(`/sme/periodic/period/${periodLabel}`);
        setCurrentPeriod(detail.data);
      } else {
        setCurrentPeriod(null);
      }
    } catch (e) {
      if (e.response?.status !== 404) setError(e);
    }
    setLoading(false);
  }, [selectedYear, selectedMonth, periodLabel]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const refreshPeriod = useCallback(async () => {
    try {
      const detail = await client.get(`/sme/periodic/period/${periodLabel}`);
      setCurrentPeriod(detail.data);
      // Also refresh evolution
      const ev = await client.get("/sme/periodic/evolution?months=12");
      setEvolution(ev.data?.periods || []);
    } catch {}
  }, [periodLabel]);

  return {
    // Period selection
    selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth,
    periodLabel,
    // Data
    currentPeriod,
    evolution,
    ytdData,
    trailing12,
    allPeriods,
    // State
    loading, error,
    // Actions
    refresh: fetchAll,
    refreshPeriod,
  };
}
